import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import DataSourceSyncFrequencyAction, {
	type DataSourceSyncFrequencyActionProps,
} from '@databox/ember-integrations/lib/components/DataSourceSyncFrequencyAction/DataSourceSyncFrequencyAction';

import type { ReactIntegrations, Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';
import { getShadowDomMode } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';

import type Source from 'webapp/models-custom/source';
import { mapSourceToDataSource } from 'webapp/models/helpers/react-model-mapper';

interface Args {
	dataSource: Source;
}

export default class SourceSyncFrequencyComponent extends Component<Args> {
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('billing') declare billingService: Service.Billing;
	@service('dataset-events') declare datasetEventsService: Service.DatasetEvents;
	@service('react') declare reactService: Service.React;

	node: HTMLDivElement | null = null;
	reactElement: ReactIntegrations.Root = null;

	get syncFrequency(): number {
		return this.args.dataSource.syncInterval;
	}

	@action
	handleIsChangeRestricted(): boolean {
		if (!this.args.dataSource.numericId || this.accountService.handleDemo()) {
			return true;
		}

		return false;
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node as HTMLDivElement;
		this.reactElement = this.reactService.create(this.node);

		this.renderSyncFrequency();

		this.eventManagerService.on(AppEvent.THEME_CHANGED, this, this.renderSyncFrequency);
		this.eventManagerService.on(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED, this, () => {
			this.reactService.unmount(this.reactElement);
			this.reactElement = this.reactService.create(node as HTMLDivElement);
			this.renderSyncFrequency();
		});
	}

	renderSyncFrequency() {
		const props: DataSourceSyncFrequencyActionProps = {
			isDarkThemeActive: this.navigationService.theme.isDark,
			dataSource: mapSourceToDataSource(
				this.args.dataSource,
				this.currentUserService.numericId,
			),
			isWhiteLabeled: this.accountService.whiteLabelingEnabled,
			onIsChangeRestricted: this.handleIsChangeRestricted,
			isPayingCustomer: this.billingService.isPayingCustomer,
			onAfterChange: this.datasetEventsService.handleChangeSyncInterval,
			trackingOrigin: 'data_management',
		};

		if (this.reactElement) {
			this.reactService.render(
				this.reactElement,
				DataSourceSyncFrequencyAction(props),
				getShadowDomMode(this.accountService.email),
			);
		}
	}

	@action
	teardownView() {
		this.reactService.unmount(this.reactElement);
		this.eventManagerService.off(AppEvent.THEME_CHANGED, this);
		this.eventManagerService.off(AppEvent.DATAMANAGER_SOURCES_CONTENT_CHANGED, this);
	}

	@action
	updateReactComponent(): void {
		this.renderSyncFrequency();
	}
	<template>
		<div
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			{{didUpdate this.updateReactComponent this.syncFrequency}}
		></div>
	</template>
}
